import { useState, ChangeEvent } from 'react';
import { TextField } from '@material-ui/core';
import {
  type SanitizedTextFieldProps,
  validateSanitizedTextField,
} from './sanitizedTextField.validation';
import {
  mergeWithDefaultUiOptions,
  transformValue,
} from './sanitizedTextField.utils';
import type { ValidationOptions } from './sanitizedTextField.types';

/**
 * Custom field extension component for sanitized text input with configurable validation rules and transformations.
 * This component is used in the scaffolder to ensure that user input is sanitized and validated according to specific rules.
 * The validation is performed both in real-time (onChange) and by the extension's validation property in extensions.ts (onSubmit).
 *
 * @example
 *  ```yaml
 * parameters:
 *   properties:
 *     name:
 *       title: Name
 *       type: string
 *       description: Enter a variable name
 *       ui:field: SanitizedTextField
 *       ui:options:
 *         placeholder: 'myVariableName'
 *         minLength: 3
 *         # camelCase
 *         customRegex: '^[a-z]+(?:[A-Z][a-z0-9]*)*$'
 * ```
 */
export const SanitizedTextField = ({
  onChange,
  rawErrors,
  required,
  disabled,
  readonly,
  schema,
  formData,
  uiSchema,
}: SanitizedTextFieldProps) => {
  const [value, setValue] = useState<string>(formData || '');
  const [localError, setLocalError] = useState<string | undefined>(undefined);

  // Extract UI options from the yaml schema and merge with defaults
  const options = mergeWithDefaultUiOptions(uiSchema);

  const {
    autofocus,
    placeholder,
    variant,
    minLength,
    maxLength,
    allowSpecialChars,
    disableUppercase,
    disableSpaces,
    transformToKebabCase,
    customRegex,
  } = options;

  const validationOptions: ValidationOptions = {
    required: !!required,
    minLength,
    maxLength,
    allowSpecialChars,
    disableUppercase,
    disableSpaces,
    transformToKebabCase,
    customRegex,
  };

  // Raw errors are provided by the form validation (e.g., required field)
  const hasRawErrors = rawErrors && rawErrors.length > 0;

  // If there are raw errors, we don't want to show local errors
  const errorMessage = hasRawErrors ? '' : localError;
  const helperText = errorMessage || schema.description;

  const validateField = (input: string): string | undefined => {
    const errors = validateSanitizedTextField(input, {
      schema: { required: validationOptions.required },
      uiSchema: { 'ui:options': validationOptions },
    });

    return errors.length > 0 ? errors[0] : undefined;
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const transformedValue = transformValue(
      e.target.value,
      transformToKebabCase,
    );

    setValue(transformedValue);

    const error = validateField(transformedValue);
    setLocalError(error);

    onChange(transformedValue);
  };

  return (
    <TextField
      fullWidth
      label={schema.title}
      helperText={helperText}
      required={required}
      disabled={disabled || readonly}
      error={!!errorMessage}
      onChange={handleChange}
      value={value}
      variant={variant}
      placeholder={placeholder}
      margin="normal"
      // eslint-disable-next-line jsx-a11y/no-autofocus -- Popular pattern in backstage scaffolding wizards
      autoFocus={autofocus}
      onBlur={() => {
        const error = validateField(value);
        setLocalError(error);
      }}
      inputProps={{
        maxLength: maxLength + 5, // Allow slightly over to show validation error
      }}
    />
  );
};
